import { estabelecimento } from "../Estabelecimento/estabelecimento"

// ===== CONSTANTES DE EXPIRAÇÃO =====

/**
 * Obtém o tempo de expiração em milissegundos
 * Usa o valor configurado no estabelecimento ou 12 horas como padrão
 * @returns {number} - Tempo de expiração em milissegundos
 */
const getTempoExpiracaoMs = () => {
    const horas = estabelecimento?.qrCode?.horasSessaoQRCode || 12
    return horas * 60 * 60 * 1000
}

/**
 * Obtém o tempo de expiração em horas
 * @returns {number} - Tempo de expiração em horas
 */
const getTempoExpiracaoHoras = () => {
    return estabelecimento?.qrCode?.horasSessaoQRCode || 12
}

// ===== FUNÇÕES AUXILIARES PARA COOKIES =====

/**
 * Salva um cookie com as configurações
 * @param {string} name - Nome do cookie
 * @param {object} value - Valor a ser salvo (será convertido para JSON)
 * @param {number} hours - Horas de expiração (usa configuração do estabelecimento por padrão)
 */
const setCookie = (name, value, hours = null) => {
    const expiracaoHoras = hours ?? getTempoExpiracaoHoras()
    try {
        const expiracao = new Date()
        expiracao.setTime(expiracao.getTime() + (expiracaoHoras * 60 * 60 * 1000))
        const cookieValue = encodeURIComponent(JSON.stringify(value))
        document.cookie = `${name}=${cookieValue}; expires=${expiracao.toUTCString()}; path=/; SameSite=Lax`
        return true
    } catch (e) {
        console.error(`Erro ao salvar cookie ${name}:`, e)
        return false
    }
}

/**
 * Lê um cookie
 * @param {string} name - Nome do cookie
 * @returns {object|null} - Objeto parseado ou null se não encontrado
 */
const getCookie = (name) => {
    try {
        const nameEQ = name + "="
        const cookies = document.cookie.split(';')
        
        for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i].trim()
            if (cookie.indexOf(nameEQ) === 0) {
                const value = cookie.substring(nameEQ.length)
                return JSON.parse(decodeURIComponent(value))
            }
        }
        return null
    } catch (e) {
        console.error(`Erro ao ler cookie ${name}:`, e)
        return null
    }
}

/**
 * Remove um cookie
 * @param {string} name - Nome do cookie
 */
const deleteCookie = (name) => {
    try {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`
        return true
    } catch (e) {
        console.error(`Erro ao remover cookie ${name}:`, e)
        return false
    }
}

// ===== FUNÇÕES DE GERENCIAMENTO DE SESSÃO QR CODE =====

/**
 * Valida o tempo da sessão do usuário
 */
export const validaSessaoQRCode = (time, estabelecimento) => {
    time = Number(time)
    let date = new Date(time)
    let today = new Date()

    const tempoSessao = getTempoExpiracaoMs()

    // Se o intervalo de tempo for maior que o tempo limite estipulado
    if ((date.getTime() + tempoSessao) < today.getTime()) {
        estabelecimento.qrCode.ativo = false
        limparConfiguracaoQRCodeExpirada()
    } else {
        estabelecimento.qrCode.ativo = true
        atualizarCookieQRCode()
    }
}

/**
 * Limpa todas as configurações QR Code expiradas (apenas cookies agora)
 */
export const limparConfiguracaoQRCodeExpirada = () => {
    try {
        deleteCookie('qrcode-config')
        console.log('Configurações QR Code removidas do cookie')
    } catch (e) {
        console.error('Erro ao limpar configurações QR Code:', e)
    }
}

/**
 * Atualiza o cookie com as configurações atuais
 */
export const atualizarCookieQRCode = () => {
    try {
        const configuracoesStorage = getCookie('qrcode-config')
        if (configuracoesStorage && configuracoesStorage.empresa === estabelecimento.urlEmpresa) {
            // Atualiza timestamp e salva novamente
            configuracoesStorage.timestampAcesso = new Date().getTime()
            setCookie('qrcode-config', configuracoesStorage)
            console.log('Cookie QR Code atualizado')
        }
    } catch (e) {
        console.error('Erro ao atualizar cookie QR Code:', e)
    }
}

/**
 * Cria ou recupera um identificador único para o usuário em modo QR Code
 * Usa cookie para persistência ao invés de localStorage
 */
export const criaIdentificadorUsuarioQRCode = () => {
    const EXPIRACAO = getTempoExpiracaoMs()
    const gerarNovoId = () => 'client_' + Math.random().toString(36).slice(2, 11)

    const obterOuCriarIdentificador = () => {
        // Busca identificadores existentes no cookie
        let identificadores = getCookie('identificadorQRCode') || {}
        
        if (
            // Se não existir um identificador para o estabelecimento
            !identificadores[estabelecimento.urlEmpresa] || 
            // Ou se o tempo de expiração for maior que o tempo limite
            new Date().getTime() - identificadores[estabelecimento.urlEmpresa].ultimoAcesso > EXPIRACAO
        ) {
            // Cria um novo identificador
            identificadores[estabelecimento.urlEmpresa] = {
                ultimoAcesso: new Date().getTime(),
                id: gerarNovoId()
            }
            setCookie('identificadorQRCode', identificadores)
        } else {
            // Atualiza o último acesso se o identificador ainda é válido
            identificadores[estabelecimento.urlEmpresa].ultimoAcesso = new Date().getTime()
            setCookie('identificadorQRCode', identificadores)
        }

        return identificadores[estabelecimento.urlEmpresa].id
    }

    return obterOuCriarIdentificador()
}

/**
 * Verifica se existe uma configuração QR Code válida no cookie
 * @returns {object|null} - Configuração válida ou null
 */
export const verificarConfiguracaoQRCodeValida = () => {
    try {
        const configuracoesStorage = getCookie('qrcode-config')
        
        if (!configuracoesStorage) {
            return null
        }

        // Verifica se a configuração não expirou usando tempo centralizado
        const tempoExpiracao = getTempoExpiracaoMs()
        const agora = new Date().getTime()
        
        if (configuracoesStorage.timestampAcesso && 
            (agora - configuracoesStorage.timestampAcesso) < tempoExpiracao &&
            configuracoesStorage.empresa === estabelecimento.urlEmpresa) {
            return configuracoesStorage
        } else {
            // Remove configurações expiradas
            limparConfiguracaoQRCodeExpirada()
            return null
        }
    } catch (e) {
        console.error('Erro ao verificar configuração QR Code válida:', e)
        return null
    }
}

/**
 * Sincroniza configurações QR Code salvando apenas no cookie
 * @param {object} configuracao - Configuração a ser salva
 */
export const sincronizarConfiguracaoQRCode = (configuracao) => {
    try {
        if (!configuracao) return

        // Salva apenas no cookie
        const sucesso = setCookie('qrcode-config', configuracao)
        
        if (sucesso) {
            console.log('Configuração QR Code salva no cookie')
        } else {
            console.error('Falha ao salvar configuração QR Code no cookie')
        }
    } catch (e) {
        console.error('Erro ao sincronizar configuração QR Code:', e)
    }
}